import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import {
  Announcement,
  AnnouncementTheme,
  announcementText,
} from "../../services/announcement/announcement.models";
import { AnnouncementClientApiService } from "../../services/announcement/announcement-client-api.service";
import { resolveAnnouncementTheme } from "./announcement-modal.component";

/** TOP_BANNER display style — a slim dismissible strip instead of the full modal. */
@Component({
  selector: "app-announcement-banner",
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: "./announcement-banner.component.html",
  styleUrl: "./announcement-banner.component.scss",
})
export class AnnouncementBannerComponent implements OnInit, OnChanges {
  @Input({ required: true }) announcement!: Announcement;
  @Input() previewMode = false;
  /** Renders inside the simulator's screen (absolute) instead of over the whole page (fixed). */
  @Input() embedded = false;
  @Input() initialPreviewLang = "en";
  @Output() closed = new EventEmitter<void>();

  previewLang = "en";

  constructor(
    private announcementClientApi: AnnouncementClientApiService,
    private translateService: TranslateService,
    private router: Router,
  ) {}

  get lang(): string {
    return this.previewMode ? this.previewLang : this.translateService.currentLang || "en";
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["initialPreviewLang"] && this.previewMode) {
      this.previewLang = this.initialPreviewLang;
    }
  }

  get feature() {
    return (this.announcement.features ?? [])[0];
  }

  /**
   * A CTA whose target is blank can only render a button that does nothing when clicked, so the
   * strip leaves it out. NONE is the admin's way of saying "no button" in the first place.
   */
  get showCta(): boolean {
    const feature = this.feature;
    return !!feature && feature.ctaType !== "NONE" && !!feature.ctaTarget?.trim();
  }

  /**
   * The banner used to paint itself with hardcoded colours, so the Colours step had no effect
   * on it and every banner came out the same dark strip. It now honours the same theme the
   * modal does — panel colours for the strip, accent colours for the button.
   */
  get theme(): Required<AnnouncementTheme> {
    return resolveAnnouncementTheme(this.announcement.theme);
  }

  text(map: Record<string, string> | undefined | null): string {
    return announcementText(map, this.lang);
  }

  ngOnInit(): void {
    this.previewLang = this.previewMode ? this.initialPreviewLang : this.lang;
    this.markSeen("SEEN");
  }

  onCtaClick(): void {
    if (this.previewMode) return;
    const feature = this.feature;
    if (!feature) return;
    this.markSeen("CLICKED", feature.id);
    if (feature.ctaType === "INTERNAL_ROUTE" && feature.ctaTarget) {
      this.router.navigateByUrl(feature.ctaTarget);
    } else if (feature.ctaType === "LINK" && feature.ctaTarget) {
      window.open(feature.ctaTarget, "_blank", "noopener,noreferrer");
    }
  }

  close(): void {
    if (this.embedded) return;
    this.markSeen("DISMISSED");
    this.closed.emit();
  }

  private markSeen(status: "SEEN" | "DISMISSED" | "CLICKED", featureId?: string): void {
    if (this.previewMode || !this.announcement.slug) return;
    this.announcementClientApi.markSeen(this.announcement.slug, { status, featureId: featureId ?? null }).subscribe({ error: () => undefined });
  }
}
